import React, { useState } from 'react';

/**
 * The test catalogue, and the record of what was run against it.
 *
 * Wireless is deterministic — the question set is fixed and known before the
 * engagement starts — so the catalogue is the methodology, visible rather than
 * implied. Compliance references are shown per test because "we ran twenty-one
 * checks" is a weaker statement than "we evidenced NIST SP 800-153 §3.1.1".
 *
 * A recorded status is as much of a deliverable as a finding. `passed` and
 * `not_authorized` both belong in the report: one says the control held, the
 * other says why a test was not performed. Only `failed` promotes to a finding.
 */

const CATEGORY_META = {
  recon: { label: 'Reconnaissance', hint: 'Passive. Runs under any rules of engagement.' },
  psk: { label: 'WPA2/WPA3-Personal', hint: 'Pre-shared key networks.' },
  enterprise: { label: 'WPA2/WPA3-Enterprise (802.1X)', hint: 'Where the high-impact findings live.' },
  rogue: { label: 'Rogue infrastructure', hint: 'Impersonation and resilience.' },
  post_assoc: { label: 'Post-association', hint: 'Only once access is obtained.' },
};

const CATEGORY_ORDER = ['recon', 'psk', 'enterprise', 'rogue', 'post_assoc'];

const AUTH_LABELS = {
  deauth_authorized: 'Deauthentication',
  rogue_ap_authorized: 'Rogue AP',
  wps_authorized: 'WPS',
  client_attack_authorized: 'Client attack',
};

const STATUS_STYLES = {
  passed: { label: 'Passed', cls: 'text-emerald-400 bg-emerald-950/40 border-emerald-500/30' },
  failed: { label: 'Failed', cls: 'text-red-400 bg-red-950/40 border-red-500/40' },
  inconclusive: { label: 'Inconclusive', cls: 'text-amber-400 bg-amber-950/30 border-amber-500/30' },
  not_applicable: { label: 'N/A', cls: 'text-slate-400 bg-slate-800/50 border-slate-700' },
  not_authorized: { label: 'Not authorised', cls: 'text-red-300 bg-red-950/30 border-red-500/30' },
  not_run: { label: 'Open', cls: 'text-indigo-300 bg-indigo-950/40 border-indigo-500/30' },
  running: { label: 'Running', cls: 'text-indigo-300 bg-indigo-950/40 border-indigo-500/30' },
  error: { label: 'Error', cls: 'text-red-400 bg-red-950/40 border-red-500/40' },
  timed_out: { label: 'Timed out', cls: 'text-amber-400 bg-amber-950/30 border-amber-500/30' },
};

// What an operator records by hand. `running` and `not_authorized` are set by
// the system, so they are deliberately absent.
const RECORDABLE = ['passed', 'failed', 'inconclusive', 'not_applicable', 'error', 'timed_out'];

const AUTO_EVALUATED = ['W-01', 'W-02', 'W-03', 'W-04'];

// Slugs the agent knows how to run. Kept in step with
// services/wireless_dispatch.DISPATCHABLE — the server refuses anything else,
// so a drift here shows up as a 400 rather than as a silent no-op.
const DISPATCHABLE = ['W-10', 'W-11', 'W-12', 'W-31'];

const TestCatalogue = ({
  tests = [],
  runs = [],
  roe = {},
  targets = [],
  busy = false,
  onEvaluateRecon,
  onCreateRun,
  onUpdateRun,
  onPromote,
  onToggleRoE,
  onDispatch,
  onStop,
}) => {
  const [expanded, setExpanded] = useState(null);
  const [chosenTarget, setChosenTarget] = useState({});
  // Drafts are per-run and local until saved. A textarea bound straight to the
  // server value would lose what an operator was typing every time the poll
  // loop refreshed the runs list.
  const [drafts, setDrafts] = useState({});

  const draftFor = (run) => drafts[run.id] || {
    raw_output: run.raw_output || '',
    notes: run.notes || '',
  };
  const setDraft = (run, field, value) =>
    setDrafts((prev) => ({
      ...prev,
      [run.id]: { ...draftFor(run), [field]: value },
    }));
  const clearDraft = (run) =>
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[run.id];
      return next;
    });

  // Only authorised networks can be dispatched against — the server refuses the
  // rest, and offering them here would be an invitation to a refusal.
  const authorised = targets.filter((t) => t.authorized);

  // Runs arrive newest first, so the first match is the current one.
  const latestRunFor = (testId) => runs.find((r) => r.test_id === testId);

  const grouped = CATEGORY_ORDER.map((key) => ({
    key,
    meta: CATEGORY_META[key] || { label: key, hint: '' },
    items: tests.filter((t) => t.category === key),
  })).filter((g) => g.items.length);

  if (!tests.length) {
    return (
      <div className="text-center py-12">
        <p className="text-sm text-slate-400">Catalogue not loaded</p>
        <p className="text-xs text-slate-600 mt-1">
          The wireless migration seeds it — apply <span className="font-mono">add_wireless_testing</span>.
        </p>
      </div>
    );
  }

  const recorded = runs.filter((r) => r.status !== 'not_run').length;

  return (
    <div className="space-y-8">
      {/* Control bar: what is authorised, and the one action that runs itself */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 p-3 rounded-lg bg-slate-950/40 border border-slate-800">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <span className="text-[11px] font-medium text-slate-400">Rules of engagement</span>
          {Object.entries(AUTH_LABELS).map(([key, label]) => (
            <label
              key={key}
              className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer select-none"
              title={
                roe[key]
                  ? `${label} is authorised — disruptive tests in this class will run`
                  : `${label} is not authorised — those tests will be refused and recorded as such`
              }
            >
              <input
                type="checkbox"
                checked={Boolean(roe[key])}
                onChange={(e) => onToggleRoE && onToggleRoE(key, e.target.checked)}
                className="w-3.5 h-3.5 rounded border-slate-700 bg-slate-900 text-indigo-600 focus:ring-0 focus:ring-offset-0"
              />
              <span className={roe[key] ? 'text-amber-400' : ''}>{label}</span>
            </label>
          ))}
        </div>

        <div className="flex items-center gap-3 ml-auto">
          <span className="text-[11px] text-slate-600 tabular-nums">
            {recorded} of {tests.length} recorded
          </span>
          <button
            onClick={onEvaluateRecon}
            disabled={busy}
            className="px-3 py-1.5 rounded-md bg-indigo-600 text-white text-[11px] font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50"
            title="Decides W-01 to W-04 from the survey already captured. Re-run it after authorising more targets."
          >
            {busy ? 'Evaluating…' : 'Evaluate reconnaissance'}
          </button>
        </div>
      </div>

      {/* Scope. A wireless assessment is bounded by a physical place and the
          radio that surveyed it, and both belong in the report's scope section
          — there is nowhere else in the platform that asks for them. Saved on
          blur rather than per keystroke. */}
      <div className="flex flex-wrap items-end gap-4 p-3 -mt-4 rounded-lg bg-slate-950/40 border border-slate-800">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-[10px] text-slate-500 mb-1">
            Location surveyed — appears as the scope boundary in the report
          </label>
          <input
            type="text"
            defaultValue={roe.wireless_location || ''}
            onBlur={(e) => {
              if (e.target.value !== (roe.wireless_location || '')) {
                onToggleRoE && onToggleRoE('wireless_location', e.target.value);
              }
            }}
            placeholder="e.g. Main branch, ground floor"
            className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-2 py-1.5 text-slate-300 text-[11px] focus:border-indigo-600 focus:outline-none"
          />
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[10px] text-slate-500 mb-1">Capture hardware</label>
          <input
            type="text"
            defaultValue={roe.wireless_adapter || ''}
            onBlur={(e) => {
              if (e.target.value !== (roe.wireless_adapter || '')) {
                onToggleRoE && onToggleRoE('wireless_adapter', e.target.value);
              }
            }}
            placeholder="e.g. Alfa AWUS036ACM (MT7612U)"
            className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-2 py-1.5 text-slate-300 text-[11px] focus:border-indigo-600 focus:outline-none"
          />
        </div>
      </div>

      <p className="text-[11px] text-slate-600 -mt-4 leading-relaxed">
        Four tests are decided from survey data. The rest are recorded by the operator:
        run the tool on the agent, then set the result here. Only a failure becomes a finding.
      </p>

      {grouped.map((group) => (
        <div key={group.key}>
          <div className="flex items-baseline gap-3 mb-3">
            <h4 className="text-sm font-semibold text-slate-200">{group.meta.label}</h4>
            <span className="text-[11px] text-slate-600">{group.meta.hint}</span>
            <span className="ml-auto text-[11px] text-slate-600 tabular-nums">
              {group.items.length}
            </span>
          </div>

          <div className="border border-slate-800 rounded-lg divide-y divide-slate-800/70 overflow-hidden">
            {group.items.map((test) => {
              const gated = test.disruptive && test.authorization_key;
              const permitted = gated ? Boolean(roe[test.authorization_key]) : true;
              const run = latestRunFor(test.id);
              const status = run ? STATUS_STYLES[run.status] : null;
              const isOpen = expanded === test.id;
              const auto = AUTO_EVALUATED.includes(test.slug);

              return (
                <div key={test.id} className="bg-slate-950/40">
                  <div
                    className="p-3 hover:bg-slate-900/40 transition-colors cursor-pointer"
                    onClick={() => setExpanded(isOpen ? null : test.id)}
                  >
                    <div className="flex items-start gap-3">
                      <span className="font-mono text-[11px] text-slate-500 pt-0.5 w-10 shrink-0">
                        {test.slug}
                      </span>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm text-slate-200">{test.name}</span>

                          {status && (
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${status.cls}`}>
                              {status.label}
                            </span>
                          )}
                          {run?.finding_id && (
                            <span className="px-1.5 py-0.5 rounded text-[10px] font-medium border text-purple-300 bg-purple-950/30 border-purple-500/30">
                              Finding #{run.finding_id}
                            </span>
                          )}
                          {test.disruptive && (
                            <span
                              className={`px-1.5 py-0.5 rounded text-[10px] font-medium border ${
                                permitted
                                  ? 'text-amber-400 bg-amber-950/40 border-amber-500/30'
                                  : 'text-red-400 bg-red-950/40 border-red-500/40'
                              }`}
                              title={
                                permitted
                                  ? 'Disruptive, and authorised by the rules of engagement'
                                  : 'Disruptive and NOT authorised — a run will be refused and recorded'
                              }
                            >
                              {permitted ? 'Disruptive · permitted' : 'Blocked by RoE'}
                            </span>
                          )}
                          {auto && (
                            <span className="text-[10px] text-slate-600" title="Decided from survey data">
                              auto
                            </span>
                          )}
                          {test.tool && (
                            <span className="font-mono text-[10px] text-slate-600">{test.tool}</span>
                          )}
                        </div>

                        <p className="text-xs text-slate-500 mt-1 leading-relaxed">{test.objective}</p>

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2">
                          {[test.nist_800_153_ref, test.nist_800_97_ref, test.pci_dss_ref]
                            .filter(Boolean)
                            .map((ref) => (
                              <span key={ref} className="text-[10px] text-slate-500">{ref}</span>
                            ))}
                          {test.mitre_technique && (
                            <span className="text-[10px] font-mono text-slate-600">
                              {test.mitre_technique}
                            </span>
                          )}
                          {gated && !permitted && (
                            <span className="text-[10px] text-red-400/80">
                              Requires “{AUTH_LABELS[test.authorization_key]}” authorisation on the scope
                            </span>
                          )}
                        </div>
                      </div>

                      <span className="text-slate-700 text-xs pt-0.5 shrink-0">
                        {isOpen ? '−' : '+'}
                      </span>
                    </div>
                  </div>

                  {isOpen && (
                    <div className="px-3 pb-3 pl-16 space-y-3 border-t border-slate-800/70 pt-3">
                      {run?.raw_output && (
                        <pre className="text-[11px] text-slate-400 bg-slate-950/70 border border-slate-800 rounded p-3 overflow-x-auto whitespace-pre-wrap max-h-72 overflow-y-auto leading-relaxed">
                          {run.raw_output}
                        </pre>
                      )}

                      {/* Dispatchable tests: pick an authorised network and run
                          it on the agent. Everything else is recorded by hand. */}
                      {DISPATCHABLE.includes(test.slug) && run?.status !== 'running' && (
                        <div className="flex flex-wrap items-center gap-2">
                          <select
                            value={chosenTarget[test.id] || ''}
                            onChange={(e) =>
                              setChosenTarget((prev) => ({ ...prev, [test.id]: e.target.value }))
                            }
                            disabled={busy || !authorised.length}
                            className="rounded-md bg-slate-950/70 border border-slate-800 px-2 py-1 text-slate-300 text-[11px] focus:border-indigo-600 focus:outline-none disabled:opacity-50"
                          >
                            <option value="">
                              {authorised.length
                                ? 'Choose an authorised network…'
                                : 'No networks authorised yet'}
                            </option>
                            {authorised.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.ssid || '‹hidden›'} · {t.bssid}
                                {t.channel ? ` · ch ${t.channel}` : ''}
                              </option>
                            ))}
                          </select>

                          <button
                            onClick={() =>
                              onDispatch && onDispatch(test, Number(chosenTarget[test.id]))
                            }
                            disabled={busy || !chosenTarget[test.id] || !permitted}
                            className="px-3 py-1.5 rounded-md bg-emerald-700/80 text-white text-[11px] font-medium hover:bg-emerald-700 transition-colors disabled:opacity-40"
                            title={
                              permitted
                                ? `Runs ${test.tool} on the agent for up to ${test.default_timeout_s}s`
                                : 'Not authorised by the rules of engagement'
                            }
                          >
                            Run on agent
                          </button>

                          <span className="text-[10px] text-slate-600">
                            up to {Math.round((test.default_timeout_s || 300) / 60)} min
                          </span>
                        </div>
                      )}

                      {run?.status === 'running' && (
                        <div className="flex flex-wrap items-center gap-3 text-[11px] text-indigo-300">
                          <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
                            Running on the agent — the survey is paused, and no other
                            test can use the radio
                          </span>
                          <button
                            onClick={() => onStop && onStop(run)}
                            disabled={busy}
                            className="px-2.5 py-1 rounded-md border border-slate-700 text-slate-300 text-[10px] hover:bg-slate-800 hover:border-slate-600 transition-colors disabled:opacity-50"
                            title="Kill the tool now. Whatever it captured is still analysed, so a shorter window still produces a result."
                          >
                            Stop
                          </button>
                        </div>
                      )}

                      {!run ? (
                        !DISPATCHABLE.includes(test.slug) && (
                          <button
                            onClick={() => onCreateRun && onCreateRun(test)}
                            disabled={busy}
                            className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 text-[11px] hover:bg-slate-800 transition-colors disabled:opacity-50"
                          >
                            Open a run
                          </button>
                        )
                      ) : (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[11px] text-slate-500 mr-1">Record:</span>
                          {RECORDABLE.map((s) => (
                            <button
                              key={s}
                              onClick={() => onUpdateRun && onUpdateRun(run, { status: s })}
                              disabled={busy || Boolean(run.finding_id)}
                              className={`px-2 py-1 rounded text-[10px] border transition-colors disabled:opacity-40 ${
                                run.status === s
                                  ? STATUS_STYLES[s].cls
                                  : 'text-slate-500 border-slate-800 hover:border-slate-600 hover:text-slate-300'
                              }`}
                            >
                              {STATUS_STYLES[s].label}
                            </button>
                          ))}

                          {run.status === 'failed' && !run.finding_id && (
                            <button
                              onClick={() => onPromote && onPromote(run, test)}
                              disabled={busy}
                              className="ml-auto px-3 py-1.5 rounded-md bg-purple-700/80 text-white text-[11px] font-medium hover:bg-purple-700 transition-colors disabled:opacity-50"
                              title="Draft a finding from this failure. The description comes from the catalogue and is expected to be rewritten."
                            >
                              Promote to finding
                            </button>
                          )}
                          {run.finding_id && (
                            <span className="ml-auto text-[10px] text-slate-500">
                              Promoted — edit finding #{run.finding_id} in Operations
                            </span>
                          )}
                        </div>
                      )}

                      {/* Evidence and notes. Seventeen of the twenty-one tests
                          are run by hand on the agent, so this is where their
                          result actually comes from — without it a status
                          button is the only thing an operator can record, and
                          the report has nothing to show for the test. */}
                      {run && !run.finding_id && (
                        <div className="space-y-2">
                          <div>
                            <label className="block text-[10px] text-slate-500 mb-1">
                              Tool output — carried into the report as evidence
                            </label>
                            <textarea
                              rows={5}
                              value={draftFor(run).raw_output}
                              onChange={(e) => setDraft(run, 'raw_output', e.target.value)}
                              placeholder="Paste the output of the command you ran on the agent."
                              className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-2 py-1.5 text-slate-300 text-[11px] font-mono leading-relaxed focus:border-indigo-600 focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-[10px] text-slate-500 mb-1">
                              Analyst notes
                            </label>
                            <textarea
                              rows={2}
                              value={draftFor(run).notes}
                              onChange={(e) => setDraft(run, 'notes', e.target.value)}
                              placeholder="What you observed, and why the result is what it is."
                              className="w-full rounded-md bg-slate-950/70 border border-slate-800 px-2 py-1.5 text-slate-300 text-[11px] leading-relaxed focus:border-indigo-600 focus:outline-none"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={async () => {
                                await onUpdateRun(run, draftFor(run));
                                clearDraft(run);
                              }}
                              disabled={busy}
                              className="px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 text-[11px] hover:bg-slate-800 transition-colors disabled:opacity-50"
                            >
                              Save evidence
                            </button>
                            {drafts[run.id] && (
                              <button
                                onClick={() => clearDraft(run)}
                                className="text-[10px] text-slate-600 hover:text-slate-400"
                              >
                                discard changes
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {run?.finding_id && run?.notes && (
                        <p className="text-[11px] text-slate-400 leading-relaxed">{run.notes}</p>
                      )}

                      {(test.pass_means || test.fail_means) && (
                        <div className="text-[11px] text-slate-600 space-y-1 pt-1">
                          {test.pass_means && <p><span className="text-slate-500">Pass:</span> {test.pass_means}</p>}
                          {test.fail_means && <p><span className="text-slate-500">Fail:</span> {test.fail_means}</p>}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default TestCatalogue;
